/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React, { useState } from "react";
import {
  Card,
  Typography,
  Button,
  IconButton,
  makeStyles,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@material-ui/core";
import {
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  FileCopy as CopyIcon,
} from "@material-ui/icons";
import { useAuth } from "../../../../../contexts/AuthContext";

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 12,
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    padding: theme.spacing(3),
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    border: "1px solid #f0f0f0",
    overflow: "visible",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 20px rgba(0, 0, 0, 0.12)",
    },
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  statusContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    borderRadius: 16,
    padding: theme.spacing(0.5, 1.5),
    fontWeight: 500,
  },
  activeChip: {
    backgroundColor: "rgba(50, 165, 142, 0.15)",
    color: "#32a58e",
  },
  inactiveChip: {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    color: "#666",
  },
  performanceIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  highPerformance: {
    color: "#32a58e",
  },
  mediumPerformance: {
    color: "#f0ad4e",
  },
  lowPerformance: {
    color: "#d9534f",
  },
  iconContainer: {
    marginBottom: theme.spacing(1.5),
  },
  icon: {
    fontSize: 36,
    color: theme.palette.primary.main,
    opacity: 0.8,
  },
  title: {
    fontWeight: 600,
    fontSize: "1.25rem",
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
  },
  description: {
    color: "#555",
    fontSize: "0.95rem",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    height: 45,
  },
  tag: {
    display: "inline-block",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    color: "#666",
    fontSize: "0.75rem",
    fontWeight: 500,
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(2),
  },
  progressSection: {
    marginBottom: theme.spacing(2),
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    color: "#666",
    marginBottom: theme.spacing(0.5),
  },
  progress: {
    height: 8,
    borderRadius: 4,
  },
  progressGood: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#32a58e",
    },
  },
  progressWarning: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#f0ad4e",
    },
  },
  progressDanger: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#d9534f",
    },
  },
  metrics: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    color: "#666",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  metricItem: {
    marginRight: theme.spacing(2),
  },
  metricValue: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  actionButton: {
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.95rem",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.15s ease",
    "&:active": {
      transform: "scale(0.98)",
    },
  },
  reviewButton: {
    backgroundColor: "#32a58e",
    color: "white",
    "&:hover": {
      backgroundColor: "#288a74",
    },
  },
  startButton: {
    backgroundColor: "#1b2b4a",
    color: "white",
    "&:hover": {
      backgroundColor: "#152340",
    },
  },
  additionalActions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: "1px solid #f0f0f0",
  },
  actionIconButton: {
    padding: theme.spacing(1),
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },
    transition: "transform 0.15s ease",
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  actionIcon: {
    fontSize: "1.25rem",
  },
  editButton: {
    color: "#555",
  },
  openButton: {
    color: theme.palette.primary.main,
  },
  deleteButton: {
    color: "#e76f51",
  },
  shareButton: {
    color: "#555",
  },
  expandButton: {
    marginLeft: "auto",
    transition: "transform 0.3s",
  },
  expandButtonRotated: {
    transform: "rotate(180deg)",
  },
  detailsSection: {
    padding: theme.spacing(2, 0, 0),
  },
  detailItem: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
  },
  detailLabel: {
    color: "#666",
    fontWeight: 500,
  },
  detailValue: {
    color: theme.palette.text.primary,
  },
  copyLinkInput: {
    fontSize: "0.875rem",
  },
  tooltip: {
    fontSize: "0.75rem",
    backgroundColor: "rgba(0, 0, 0, 0.75)",
  },
}));

/**
 * A card component for displaying a link in the Agent Dashboard.
 *
 * @prop {string} title The title of the link.
 * @prop {string} description The description of the link.
 * @prop {Function} onOpen The function to call when the link is opened in a new tab.
 * @prop {Function} onEdit The function to call when the link is edited.
 * @prop {Function} onDelete The function to call when the link is deleted.
 * @prop {string} linkUrl The URL of the link.
 *
 * @returns A Card component with the link's title, description, and actions.
 */
function LinkCard({ title, description, onOpen, onEdit, onDelete, linkUrl }) {
  const classes = useStyles();
  const { currentUser } = useAuth();

  // State for share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Handle share dialog
  const handleShareOpen = e => {
    e.stopPropagation();
    setShareDialogOpen(true);
  };

  const handleShareClose = () => {
    setShareDialogOpen(false);
    setCopySuccess(false);
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(linkUrl || description).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  return (
    <Card className={classes.card}>
      <div className={classes.cardContent}>
        <Typography className={classes.title} noWrap title={title}>
          {title}
        </Typography>

        <Tooltip
          title={description}
          placement="top"
          classes={{ tooltip: classes.tooltip }}
        >
          <Typography className={classes.description}>{description}</Typography>
        </Tooltip>

        <div className={classes.additionalActions}>
          <div>
            <Tooltip
              title="Share link"
              placement="top"
              classes={{ tooltip: classes.tooltip }}
            >
              <IconButton
                className={`${classes.actionIconButton} ${classes.shareButton}`}
                onClick={handleShareOpen}
                size="small"
                aria-label="Share"
              >
                <ShareIcon className={classes.actionIcon} />
              </IconButton>
            </Tooltip>

            <Tooltip
              title="Open in new tab"
              placement="top"
              classes={{ tooltip: classes.tooltip }}
            >
              <IconButton
                className={`${classes.actionIconButton} ${classes.openButton}`}
                onClick={onOpen}
                size="small"
                aria-label="Open in new tab"
              >
                <OpenInNewIcon className={classes.actionIcon} />
              </IconButton>
            </Tooltip>
          </div>

          {currentUser.is_admin && (
            <div>
              <Tooltip
                title="Edit link"
                placement="top"
                classes={{ tooltip: classes.tooltip }}
              >
                <IconButton
                  className={`${classes.actionIconButton} ${classes.editButton}`}
                  onClick={onEdit}
                  size="small"
                  aria-label="Edit"
                >
                  <EditIcon className={classes.actionIcon} />
                </IconButton>
              </Tooltip>

              <Tooltip
                title="Delete link"
                placement="top"
                classes={{ tooltip: classes.tooltip }}
              >
                <IconButton
                  className={`${classes.actionIconButton} ${classes.deleteButton}`}
                  onClick={onDelete}
                  size="small"
                  aria-label="Delete"
                >
                  <DeleteIcon className={classes.actionIcon} />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleShareClose}
        aria-labelledby="share-dialog-title"
      >
        <DialogTitle id="share-dialog-title">Share Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Copy the link URL to share with others:
          </Typography>
          <TextField
            variant="outlined"
            fullWidth
            value={linkUrl || description}
            InputProps={{
              endAdornment: (
                <Tooltip
                  title={copySuccess ? "Copied!" : "Copy to clipboard"}
                  placement="top"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <IconButton edge="end" onClick={copyToClipboard}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              ),
              className: classes.copyLinkInput,
              readOnly: true,
            }}
          />
          {copySuccess && (
            <Typography
              variant="body2"
              style={{ color: "#32a58e", marginTop: 8 }}
            >
              Link copied to clipboard!
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default LinkCard;
